import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader, ChevronDown, ChevronUp } from 'lucide-react';
import L from 'leaflet';
import { STATUS } from '../../constants/appConstants';
import NotificationCard from '../common/cards/NotificationCard';
import Pagination from '../common/Pagination';
import NotificationsControls from '../notifications/NotificationsControls';
import { sanitizeCoordinatesForMap } from '../../utils/coordinateUtils';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// --- FUNÇÃO AUXILIAR PARA NORMALIZAÇÃO DE TEXTO ---
const normalizeText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};


// ⚡ Bolt: Wrapped in React.memo() to prevent re-renders when parent NotificationsPage state (like searchTerm) changes.
// Only re-renders when the actual status data counts change.
const NotificationsStatusDisplay = React.memo(({ data }) => {
    if (!data || data.length === 0) {
        return null;
    }
    return (
        <div className="flex space-x-4 overflow-x-auto pb-4">
            {data.map((item, index) => (
                <div
                    key={index}
                    className="flex-shrink-0 w-48 p-4 bg-white dark:bg-slate-800 rounded-lg shadow-md border-t-4"
                    style={{ borderColor: item.color || '#ccc' }}
                >
                    <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{item.count}</p>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400 truncate">{item.name}</p>
                </div>
            ))}
        </div>
    );
});

// ⚡ Bolt: Wrapped in React.memo() to prevent expensive map re-renders during text input or status filtering.
// The map only needs to re-render when the filtered notifications list or dark mode setting changes.
const NotificationsMap = React.memo(({ notifications, onMarkerClick, darkMapEnabled }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const tileLayerRef = useRef(null);
    const markersRef = useRef([]);
    const [isMapActive, setIsMapActive] = useState(false);

    useEffect(() => {
        if (mapContainerRef.current && !mapRef.current) {
            mapRef.current = L.map(mapContainerRef.current, {
                center: [-14.235, -51.925],
                zoom: 4,
                zoomControl: false,
                dragging: !L.Browser.mobile,
                tap: !L.Browser.mobile,
                touchZoom: false,
            });
            L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);
        }
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (mapRef.current && L.Browser.mobile) {
            if (isMapActive) {
                mapRef.current.dragging.enable();
                mapRef.current.touchZoom.enable();
            }
        }
    }, [isMapActive]);

    useEffect(() => {
        if (!mapRef.current) return;
        const tileUrl = getMapTileUrl(darkMapEnabled);
        if (tileLayerRef.current) {
            tileLayerRef.current.setUrl(tileUrl);
        } else {
            tileLayerRef.current = L.tileLayer(tileUrl, { attribution: getMapTileAttribution(darkMapEnabled) }).addTo(mapRef.current);
        }
    }, [darkMapEnabled]);

    useEffect(() => {
        if (!mapRef.current) return;
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];

        // Filtra notificações com coordenadas válidas e sanitiza-as
        const notificationsWithValidCoords = (notifications || [])
            .map(n => {
                if (!n || !n.coordinates) return null;
                const sanitizedCoords = sanitizeCoordinatesForMap(n.coordinates);
                if (!sanitizedCoords) return null;
                return { ...n, coordinates: sanitizedCoords };
            })
            .filter(n => n !== null);

        if (notificationsWithValidCoords.length > 0) {
            const newMarkers = notificationsWithValidCoords.map(notification => {
                const marker = L.marker([notification.coordinates.lat, notification.coordinates.lng], {
                    icon: L.divIcon({
                        html: `<svg viewBox="0 0 24 24" width="30" height="30" fill="#3b82f6" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="1"/></svg>`,
                        className: 'bg-transparent border-0', iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30]
                    })
                });
                marker.on('click', () => onMarkerClick && onMarkerClick(notification));
                marker.bindPopup(`<b>N°: ${notification.notificationNumber}</b><br />${notification.address || 'Endereço não disponível'}`);
                return marker;
            });
            const group = new L.featureGroup(newMarkers).addTo(mapRef.current);
            mapRef.current.fitBounds(group.getBounds().pad(0.2));
            markersRef.current = newMarkers;
        } else {
            mapRef.current.setView([-14.235, -51.925], 4);
        }
    }, [notifications, onMarkerClick]);

    return (
        <div className="relative rounded-lg shadow-md overflow-hidden">
            <div ref={mapContainerRef} className="h-64 md:h-80 w-full z-0" aria-label="Mapa de localização das notificações" />
            {!isMapActive && L.Browser.mobile && (
                <div
                    className="absolute inset-0 bg-black/30 backdrop-blur-sm flex flex-col items-center justify-center z-10 cursor-pointer"
                    onClick={() => setIsMapActive(true)}
                >
                    <MapPin className="h-8 w-8 text-white mb-2" />
                    <p className="text-white font-semibold">Toque para interagir com o mapa</p>
                </div>
            )}
        </div>
    );
});

const NotificationsPage = ({
    userData,
    settings,
    theme,
    onOpenNotificationModal,
    onOpenBulkNotificationModal,
    notifications = [],
    loading = true,
    usersList = []
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(STATUS.PENDENTE);
    const [currentPage, setCurrentPage] = useState(1);
    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const NOTIFICATIONS_PER_PAGE = 15;

    const isMapDark = settings?.darkMapEnabled && theme === 'dark';

    const usersMap = useMemo(() => {
        const map = new Map();
        (usersList || []).forEach(user => map.set(user.id, user));
        return map;
    }, [usersList]);

    // Normalize notifications to ensure status fallback
    const normalizedNotifications = useMemo(() => {
        const validStatuses = Object.values(STATUS);
        const normCache = new Map();
        const getNormalized = (text) => {
            if (!text || typeof text !== 'string') return '';
            if (normCache.has(text)) return normCache.get(text);
            const normalized = text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            normCache.set(text, normalized);
            return normalized;
        };

        return (notifications || []).map(notification => {
            if (!notification) return null;

            let status = notification.status;
            // Normalize casing and fallback to 'pendente' if invalid/null
            if (!status || !validStatuses.includes(status.toLowerCase())) {
                status = STATUS.PENDENTE;
            } else {
                status = status.toLowerCase();
            }


            const technicianName = notification.assignedTechnician ? usersMap.get(notification.assignedTechnician)?.displayName : '';
            const searchString = `
                ${getNormalized(notification.notificationNumber)} |||
                ${getNormalized(technicianName)} |||
                ${getNormalized(notification.city)} |||
                ${getNormalized(notification.neighborhood)} |||
                ${getNormalized(notification.address)}
            `.toLowerCase();

            // Pre-calculate dueDate timestamp for O(1) comparison in sort
            let dueTimestamp = null;
            if (notification.dueDate) {
                const d = new Date(notification.dueDate);
                if (!isNaN(d.getTime())) {
                    dueTimestamp = d.getTime();
                }
            }

            return { ...notification, status, searchString, dueTimestamp };
        }).filter(n => n !== null);
    }, [notifications, usersMap]);

    const filteredNotifications = useMemo(() => {
        const normalizedSearch = normalizeText(searchTerm);
        return normalizedNotifications
            .filter(notification => {
                let matchesStatus;
                if (statusFilter === 'Todos') {
                    matchesStatus = true;
                } else if (statusFilter === STATUS.A_RESPONDER) {
                    matchesStatus = notification.status === STATUS.FINALIZADA && notification.responseStatus !== 'Respondida';
                } else {
                    matchesStatus = notification.status === statusFilter;
                }

                if (!matchesStatus) return false;

                if (!normalizedSearch) return true;
                return notification.searchString && notification.searchString.includes(normalizedSearch);
            })
            .sort((a, b) => {
                const timeA = a.dueTimestamp;
                const timeB = b.dueTimestamp;

                if (timeA === null) return 1; // Move invalid or null dates to the end
                if (timeB === null) return -1;

                return timeA - timeB;
            });
    }, [normalizedNotifications, searchTerm, statusFilter]);

    const totalPages = Math.ceil(filteredNotifications.length / NOTIFICATIONS_PER_PAGE);
    const paginatedNotifications = useMemo(() => {
        const startIndex = (currentPage - 1) * NOTIFICATIONS_PER_PAGE;
        const endIndex = startIndex + NOTIFICATIONS_PER_PAGE;
        return filteredNotifications.slice(startIndex, endIndex);
    }, [currentPage, filteredNotifications]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    const notificationsStatusData = useMemo(() => {
        const counts = {
            [STATUS.PENDENTE]: 0,
            [STATUS.EM_EXECUCAO]: 0,
            [STATUS.FINALIZADA]: 0
        };
        normalizedNotifications.forEach(notification => {
            if (counts[notification.status] !== undefined) {
                counts[notification.status]++;
            }
        });
        return [
            { name: 'Pendentes', count: counts[STATUS.PENDENTE], color: '#ef4444' },
            { name: 'Em execução', count: counts[STATUS.EM_EXECUCAO], color: '#3b82f6' },
            { name: 'Finalizadas', count: counts[STATUS.FINALIZADA], color: '#22c55e' },
        ];
    }, [normalizedNotifications]);

    const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), []);
    const handleStatusChange = useCallback((e) => setStatusFilter(e.target.value), []);

    if (loading) {
        return <div className="flex justify-center items-center h-64"><Loader className="animate-spin h-16 w-16 text-blue-500" /></div>;
    }

    return (
        <div className="p-4 space-y-6">
            <section className="space-y-6">
                <NotificationsStatusDisplay data={notificationsStatusData} />
                <div>
                    <div
                        className="flex items-center justify-between bg-slate-100 dark:bg-slate-700/50 p-3 rounded-lg cursor-pointer select-none"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isMapExpanded}
                        onClick={() => setIsMapExpanded(prev => !prev)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsMapExpanded(prev => !prev); } }}
                    >
                        <span className="font-medium text-slate-700 dark:text-slate-200">Mapa de Notificações</span>
                        {isMapExpanded ? <ChevronUp size={20} className="text-slate-500 dark:text-slate-400" /> : <ChevronDown size={20} className="text-slate-500 dark:text-slate-400" />}
                    </div>
                    {isMapExpanded && (
                        <div className="mt-3">
                            <NotificationsMap notifications={filteredNotifications} darkMapEnabled={isMapDark} onMarkerClick={onOpenNotificationModal} />
                        </div>
                    )}
                </div>
            </section>

            <section>
                <NotificationsControls
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    statusFilter={statusFilter}
                    onStatusChange={handleStatusChange}
                    onNewClick={onOpenNotificationModal}
                    onNewBulkClick={onOpenBulkNotificationModal}
                    userRole={userData?.role}
                />
                <div className="space-y-4">
                    {paginatedNotifications.length > 0 ? (
                        paginatedNotifications.map(notification => {
                            const technician = notification.assignedTechnician ? usersMap.get(notification.assignedTechnician) : null;
                            return (
                                <NotificationCard
                                    key={notification.id}
                                    notification={notification}
                                    onCardClick={onOpenNotificationModal}
                                    technician={technician}
                                />
                            );
                        })
                    ) : (
                        <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                            Nenhuma notificação encontrada com os filtros atuais.
                        </p>
                    )}
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </section>
        </div>
    );
};

export default NotificationsPage;
